import { Link } from "react-router-dom";
import { useTenantBranding } from "@/contexts/BrandContext";

/**
 * ArmIQ Logo
 *
 * variant:
 *   "lockup"    – DEFAULT. Horizontal lockup: pitcher icon on the LEFT + "ArmIQ" wordmark on the RIGHT.
 *                 Use everywhere (header, footer, side-nav, auth panels). Looks great on dark surfaces.
 *   "wordmark"  – text-only "ArmIQ". Use only when space is extremely tight.
 *   "image"     – the full vertical lockup PNG (with built-in wordmark + tagline + shield). Use for hero moments only.
 *   "stack"     – PNG image (no text wrapping). Use for splash / loading screens.
 *
 * White-label behavior:
 *   When the calling tenant is an Enterprise customer with DNS-verified
 *   custom branding, the lockup variant swaps the ArmIQ pitcher icon
 *   for the tenant's logo_url and the wordmark for display_name.
 *   Any non-lockup variant (image / stack / wordmark) keeps the ArmIQ
 *   defaults — those are deliberate brand moments.
 */
export default function Logo({
  size = "md",
  variant = "lockup",
  linkTo = "/",
  className = "",
  inverted = false,
  dark = false,
}) {
  const { logoUrl, displayName } = useTenantBranding();
  // Wordmark font sizes
  const txtCls =
    size === "xl" ? "text-5xl" :
    size === "lg" ? "text-3xl" :
    size === "sm" ? "text-lg"  :
    "text-2xl";

  // Icon heights (square)
  const iconH =
    size === "xl" ? "h-16 w-16" :
    size === "lg" ? "h-12 w-12" :
    size === "sm" ? "h-7 w-7"   :
    "h-10 w-10";

  // Full PNG height (vertical lockup variant)
  const imgH =
    size === "xl" ? "h-44" :
    size === "lg" ? "h-32" :
    size === "sm" ? "h-10" :
    "h-14";

  // Gap between icon and wordmark
  const gapCls = size === "sm" ? "gap-2" : "gap-3";

  let body;

  if (variant === "image") {
    body = (
      <span className={`inline-flex items-center ${className}`} data-testid="logo">
        <img
          src="/biohacker-logo.png"
          alt="BioHacker Sports"
          className={`${imgH} w-auto ${dark ? "rounded-2xl bg-white p-2 ring-1 ring-white/10 shadow-2xl" : ""}`}
        />
      </span>
    );
  } else if (variant === "stack") {
    body = (
      <span className={`inline-flex flex-col items-start gap-3 ${className}`} data-testid="logo">
        <img
          src="/biohacker-logo.png"
          alt="BioHacker Sports"
          className={`${imgH} w-auto ${dark ? "rounded-2xl bg-white p-2 ring-1 ring-white/10 shadow-xl" : ""}`}
        />
      </span>
    );
  } else if (variant === "wordmark") {
    body = (
      <span className={`font-display font-black tracking-tight ${txtCls} ${className}`} data-testid="logo">
        <span style={{ color: inverted ? "#ffffff" : "var(--brand-primary)" }}>BioHacker</span>
        <span style={{ color: "var(--brand-secondary)" }}> Sports</span>
      </span>
    );
  } else {
    // "lockup" — icon LEFT + "BioHacker Sports" wordmark RIGHT.
    // White-label: swap the icon for the tenant logo and the wordmark for display_name.
    const isWL = !!logoUrl;
    body = (
      <span className={`inline-flex items-center ${gapCls} ${className}`} data-testid={isWL ? "tenant-logo" : "logo"}>
        <img
          src={isWL ? logoUrl : "/biohacker-icon.png"}
          alt={isWL ? (displayName || "Tenant") : "BioHacker Sports"}
          aria-hidden={!isWL}
          className={`${iconH} object-contain ${
            isWL
              ? "rounded-md bg-white p-1 ring-1 ring-white/10 shadow-md"
              : "drop-shadow-[0_2px_8px_rgba(217,4,41,0.35)]"
          }`}
        />
        <span className={`font-display font-black tracking-tight ${txtCls} leading-none`}>
          {isWL ? (
            <span style={{ color: inverted || dark ? "#ffffff" : "var(--brand-primary)" }}>{displayName || ""}</span>
          ) : (
            <>
              <span style={{ color: inverted || dark ? "#ffffff" : "var(--brand-primary)" }}>BioHacker</span>
              <span style={{ color: "var(--brand-secondary)" }}>&nbsp;Sports</span>
            </>
          )}
        </span>
      </span>
    );
  }
  return linkTo ? <Link to={linkTo}>{body}</Link> : body;
}
